import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { 
  Users, 
  ChevronRight,
  CheckCircle2,
  Zap,
  Shield,
  Menu,
  X,
  CreditCard,
  Sparkles,
  Code,
  QrCode,
  Camera,
  UserCheck,
  Calendar
} from 'lucide-react';

const HelpPage = ({ isOpen, onClose, initialSection = null }) => {
  const [activeSection, setActiveSection] = useState(initialSection || 'overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection]);

  const sections = {
    overview: {
      title: 'Platform Overview',
      icon: Zap,
      content: (
        <div className="space-y-5">
          <div className="text-center py-3">
            <div className="inline-flex p-3 rounded-xl bg-blue-100 mb-2">
              <Zap className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-1">BookingFlows</h2>
            <p className="text-sm text-gray-600">Complete booking & operations platform</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">How It Works</h3>
            <p className="text-xs text-gray-600 mb-3">Set up in 4 steps, then start accepting bookings</p>
            <div className="space-y-2">
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-semibold text-xs">1</div>
                <div className="text-xs">
                  <span className="font-semibold">Setup Team & Resources</span> - Define roles, add staff, register assets
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-green-50 text-green-600 rounded-full flex items-center justify-center font-semibold text-xs">2</div>
                <div className="text-xs">
                  <span className="font-semibold">Create Services</span> - Build what customers can book
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center font-semibold text-xs">3</div>
                <div className="text-xs">
                  <span className="font-semibold">Embed Widget</span> - Add to your website or share link
                </div>
              </div>
              <div className="flex gap-3 items-center bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex-shrink-0 w-7 h-7 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center font-semibold text-xs">4</div>
                <div className="text-xs">
                  <span className="font-semibold">Manage Operations</span> - Track bookings, check-ins, revenue
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Core Features</h3>
            <div className="grid md:grid-cols-2 gap-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Code className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-xs">Embeddable Widget</h4>
                </div>
                <p className="text-xs text-gray-600">Add booking form to your website</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-4 h-4 text-indigo-600" />
                  <h4 className="font-semibold text-xs">White Label</h4>
                </div>
                <p className="text-xs text-gray-600">100% your brand</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <QrCode className="w-4 h-4 text-green-600" />
                  <h4 className="font-semibold text-xs">4-Way Check-In</h4>
                </div>
                <p className="text-xs text-gray-600">QR gun, phone, manual, self-service</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck className="w-4 h-4 text-purple-600" />
                  <h4 className="font-semibold text-xs">Lead Capture</h4>
                </div>
                <p className="text-xs text-gray-600">Cart abandonment tracking</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-yellow-600" />
                  <h4 className="font-semibold text-xs">Auto Emails</h4>
                </div>
                <p className="text-xs text-gray-600">Confirmation, reminders, thank you</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-semibold text-xs">Stripe Payments</h4>
                </div>
                <p className="text-xs text-gray-600">Online payment processing</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    services: {
      title: 'Services & Booking',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Creating Services</h3>
            <p className="text-xs text-gray-600 mb-3">Build what customers can book - tours, rentals, classes, appointments</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Dynamic pricing by day/time</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Multiple duration options</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Day-specific availability</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Add-ons & upgrades</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Photo galleries</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>AI content generation</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Embedding Options</h4>
            <div className="space-y-2 text-xs">
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Widget Code:</span> Copy/paste HTML to embed on your website
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Shareable Link:</span> Direct link to booking page
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Token-Based:</span> Secure, revocable access for affiliates/partners
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-xs mb-1">Path</h4>
            <p className="text-xs text-gray-600">Customers → Services → Create Service → Get Embed Code</p>
          </div>
        </div>
      )
    },
    customerExperience: {
      title: 'Customer Experience',
      icon: Users,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Complete Customer Journey</h3>
            <div className="space-y-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-semibold text-xs">Browse & Book</span>
                </div>
                <p className="text-xs text-gray-600">Customer selects service, date, time, pays online</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-green-100 text-green-600 rounded flex items-center justify-center text-xs font-bold">2</div>
                  <span className="font-semibold text-xs">Confirmation Email</span>
                </div>
                <p className="text-xs text-gray-600">Instant confirmation with ticket link & QR code</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-purple-100 text-purple-600 rounded flex items-center justify-center text-xs font-bold">3</div>
                  <span className="font-semibold text-xs">24hr Reminder</span>
                </div>
                <p className="text-xs text-gray-600">Auto reminder before booking with ticket access</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-orange-100 text-orange-600 rounded flex items-center justify-center text-xs font-bold">4</div>
                  <span className="font-semibold text-xs">Check-In</span>
                </div>
                <p className="text-xs text-gray-600">QR scan, self check-in, or manual lookup</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs font-bold">5</div>
                  <span className="font-semibold text-xs">Waivers & Reviews</span>
                </div>
                <p className="text-xs text-gray-600">Digital signatures, review requests via ticket</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-yellow-100 text-yellow-600 rounded flex items-center justify-center text-xs font-bold">6</div>
                  <span className="font-semibold text-xs">Thank You Email</span>
                </div>
                <p className="text-xs text-gray-600">Post-booking thank you in your brand</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Key Features</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>White-labeled branding</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Lead capture</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Auto scheduling</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Team portal</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Payroll reports</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>CRM tracking</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    services: {
      title: 'Services & Booking',
      icon: Sparkles,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Creating Services</h3>
            <p className="text-xs text-gray-600 mb-3">Build what customers can book - tours, rentals, classes, appointments</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Dynamic pricing by day/time</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Multiple duration options</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Day-specific availability</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Add-ons & upgrades</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Photo galleries</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>AI content generation</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Embedding Options</h4>
            <div className="space-y-2 text-xs">
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Widget Code:</span> Copy/paste HTML to embed on your website
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Shareable Link:</span> Direct link to booking page
              </div>
              <div className="bg-gray-50 p-2 rounded">
                <span className="font-semibold">Token-Based:</span> Secure, revocable access for affiliates/partners
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-xs mb-1">Path</h4>
            <p className="text-xs text-gray-600">Customers → Services → Create Service</p>
          </div>
        </div>
      )
    },
    customerExperience: {
      title: 'Customer Experience',
      icon: Users,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Complete Customer Journey</h3>
            <div className="space-y-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-semibold text-xs">Browse & Book</span>
                </div>
                <p className="text-xs text-gray-600">Customer selects service, date, time, pays online</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-green-100 text-green-600 rounded flex items-center justify-center text-xs font-bold">2</div>
                  <span className="font-semibold text-xs">Confirmation Email</span>
                </div>
                <p className="text-xs text-gray-600">Instant confirmation with ticket link & QR code</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-purple-100 text-purple-600 rounded flex items-center justify-center text-xs font-bold">3</div>
                  <span className="font-semibold text-xs">24hr Reminder</span>
                </div>
                <p className="text-xs text-gray-600">Auto reminder before booking with ticket access</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-orange-100 text-orange-600 rounded flex items-center justify-center text-xs font-bold">4</div>
                  <span className="font-semibold text-xs">Check-In</span>
                </div>
                <p className="text-xs text-gray-600">QR scan, self check-in, or manual lookup</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs font-bold">5</div>
                  <span className="font-semibold text-xs">Waivers & Reviews</span>
                </div>
                <p className="text-xs text-gray-600">Digital signatures, review requests via ticket</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 bg-yellow-100 text-yellow-600 rounded flex items-center justify-center text-xs font-bold">6</div>
                  <span className="font-semibold text-xs">Thank You Email</span>
                </div>
                <p className="text-xs text-gray-600">Post-booking thank you in your brand</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-2">
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-xs mb-1">Digital Tickets</h4>
              <p className="text-xs text-gray-600">Branded, mobile-ready with QR codes</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-xs mb-1">Lead Capture</h4>
              <p className="text-xs text-gray-600">Track cart abandonment for follow-up</p>
            </div>
          </div>
        </div>
      )
    },
    checkInWaivers: {
      title: 'Check-In & Waivers',
      icon: QrCode,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Check-In Methods</h3>
            <p className="text-xs text-gray-600 mb-3">Optional feature - 4 flexible ways to track attendance</p>
            <div className="grid md:grid-cols-2 gap-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <QrCode className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-xs">Scanner Gun</span>
                </div>
                <p className="text-xs text-gray-600">Dedicated device with 8hr token</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Camera className="w-4 h-4 text-green-600" />
                  <span className="font-semibold text-xs">Phone Scan</span>
                </div>
                <p className="text-xs text-gray-600">Staff camera + PIN entry</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span className="font-semibold text-xs">Manual Lookup</span>
                </div>
                <p className="text-xs text-gray-600">Admin search by name/ID</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck className="w-4 h-4 text-orange-600" />
                  <span className="font-semibold text-xs">Self Check-In</span>
                </div>
                <p className="text-xs text-gray-600">Customer portal with ID + name</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Digital Waivers</h4>
            <p className="text-xs text-gray-600 mb-3">Legal signature collection with verification</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Signature capture</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>IP address logging</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Timestamp verification</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Device fingerprinting</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-xs mb-1">When to Use</h4>
            <p className="text-xs text-gray-600">High-volume events, venues requiring attendance tracking</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-xs mb-1">Path</h4>
            <p className="text-xs text-gray-600">Customers → Check-In (enable/disable, set PIN)</p>
            <p className="text-xs text-gray-600 mt-1">Customers → Waivers (enable, write text)</p>
          </div>
        </div>
      )
    },
    teamOperations: {
      title: 'Team & Scheduling',
      icon: Calendar,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-3">Operations Management</h3>
            <div className="grid md:grid-cols-2 gap-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Roles</h4>
                <p className="text-xs text-gray-600">Define positions & permissions</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Team Members</h4>
                <p className="text-xs text-gray-600">Add staff with availability</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Assets</h4>
                <p className="text-xs text-gray-600">Register resources & equipment</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Auto-Scheduler</h4>
                <p className="text-xs text-gray-600">AI assignment engine</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Payroll</h4>
                <p className="text-xs text-gray-600">Calculate earnings & tips</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Team Portal</h4>
                <p className="text-xs text-gray-600">Staff view schedules</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Scheduling Flow</h4>
            <div className="space-y-2 text-xs">
              <div>1. Create roles & add team members</div>
              <div>2. Register assets/resources</div>
              <div>3. Bookings assigned manually or via AI</div>
              <div>4. Team logs in to view their schedule</div>
              <div>5. Generate payroll reports by date range</div>
            </div>
          </div>
        </div>
      )
    },
    security: {
      title: 'Security & Access',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Access Control</h3>
            <p className="text-xs text-gray-600 mb-3">Manage who can sell your services</p>
            <div className="space-y-2">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Token-Based Embedding</h4>
                <p className="text-xs text-gray-600">Secure, non-reversible, HMAC-signed tokens for widget embedding</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Token Revocation</h4>
                <p className="text-xs text-gray-600">Instantly revoke access for affiliates or partners</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <h4 className="font-semibold text-xs mb-1">Expiring Sessions</h4>
                <p className="text-xs text-gray-600">Scanner tokens auto-expire after 8 hours</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Use Cases</h4>
            <div className="space-y-2 text-xs">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5" />
                <span>Partner no longer wanted? Revoke their embed token instantly</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5" />
                <span>Affiliate misuses widget? Remove access immediately</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5" />
                <span>Staff leaves? Scanner tokens auto-expire</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <h4 className="font-semibold text-xs mb-1">Path</h4>
            <p className="text-xs text-gray-600">Customers → Flows → Manage Tokens → Revoke</p>
          </div>
        </div>
      )
    },
    payments: {
      title: 'Payments & Revenue',
      icon: CreditCard,
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="font-semibold text-base mb-2">Payment Processing</h3>
            <p className="text-xs text-gray-600 mb-3">Accept online payments via Stripe</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Credit/debit cards</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Auto tax calculation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Platform fee (5.9% + $0.30)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Instant booking confirmation</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Setup</h4>
            <div className="space-y-2 text-xs">
              <div>1. Settings → Payments → Connect Stripe</div>
              <div>2. Complete Stripe onboarding</div>
              <div>3. Set sales tax rate</div>
              <div>4. Services now accept online bookings</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm mb-2">Reporting & Analytics</h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Revenue tracking by date range</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Booking analytics & trends</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Payroll reports & calculations</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Lead conversion metrics</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-600" />
                <span>Export to CSV</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Platform Overview', icon: Zap },
    { id: 'services', label: 'Services & Booking', icon: Sparkles },
    { id: 'customerExperience', label: 'Customer Experience', icon: Users },
    { id: 'checkInWaivers', label: 'Check-In & Waivers', icon: QrCode },
    { id: 'teamOperations', label: 'Team & Scheduling', icon: Calendar },
    { id: 'security', label: 'Security & Access', icon: Shield },
    { id: 'payments', label: 'Payments & Revenue', icon: CreditCard },
  ];

  const currentSection = sections[activeSection];
  const Icon = currentSection?.icon || Zap;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] h-[90vh] p-0 flex flex-col rounded-2xl">
        <div className="flex flex-1 overflow-hidden min-h-0 rounded-2xl">
          <div className={`${sidebarOpen ? 'w-64' : 'w-0'} md:w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto flex-shrink-0 transition-all duration-300 rounded-tl-2xl`}>
            <div className={`${sidebarOpen ? 'p-4' : 'p-0'} md:p-4`}>
              <DialogHeader className="mb-4">
                <DialogTitle className="text-base sm:text-lg">Help & Guide</DialogTitle>
              </DialogHeader>
              
              <nav className="space-y-1">
                {sidebarItems.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <ItemIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                      {activeSection === item.id && (
                        <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-w-0 rounded-tr-2xl">
            <div className="md:hidden sticky top-0 bg-white border-b z-10 p-3">
              <Button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                variant="outline"
                size="sm"
                className="w-auto"
              >
                {sidebarOpen ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Menu className="w-4 h-4" />
                )}
              </Button>
            </div>

            <div className="p-4 sm:p-8">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                  <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <h2 className="text-lg sm:text-2xl font-bold">{currentSection?.title}</h2>
              </div>
              
              <div className="text-sm sm:text-base">
                {currentSection?.content}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HelpPage;
